import styled, { css } from "styled-components";
import tw from "twin.macro";

import { DARK_THEME_SELECTOR } from "../../utils/themes";
import { BoxSizingStyle } from "../BoxSizingStyle";
import { FontStyle } from "../FontStyle";

export const StyledTableHead = styled.thead<{
  $headerVariant: "primary" | "secondary";
}>`
  ${FontStyle}
  ${BoxSizingStyle}

  ${tw`tw-text-left tw-uppercase`}
  ${tw`tw-text-theme-700 tw-font-semibold tw-text-xxs`}

  ${(props) =>
    props.$headerVariant &&
    props.$headerVariant === "primary" &&
    css`
      th {
        ${tw`tw-whitespace-nowrap tw-pl-4 tw-pr-4 tw-pb-8 tw-pt-2`}
      }

      tr {
        td:first-child,
        th:first-child {
          ${tw`tw-pl-6`}
        }
      }
    `}

    ${(props) =>
    props.$headerVariant &&
    props.$headerVariant === "secondary" &&
    css`
      th {
        ${tw`tw-whitespace-nowrap`}
      }

      tr {
        th {
          padding-bottom: 8px;
          padding-left: 16px;
        }
      }

      tr {
        th:last-child {
          padding-right: 16px;
        }
      }
    `}
`;
StyledTableHead.displayName = "StyledTableHead";

export const StyledTableBody = styled.tbody`
  ${FontStyle}
  ${BoxSizingStyle}

  ${tw`tw-truncate`}

  td {
    ${tw`tw-truncate`}
  }

  &:not(.c-table__grouped__head) {
    td {
      ${tw`tw-text-theme-700`}
    }
  }

  a {
    ${tw`tw-no-underline tw-text-primary`}
    &:hover {
      ${tw`tw-underline`}
    }
  }
`;
StyledTableBody.displayName = "StyledTableBody";

export const StyledTableWrapper = styled.div`
  ${FontStyle}
  ${BoxSizingStyle}

  ${tw`tw-mt-8 tw-mx-4 tw-overflow-x-auto tw-overflow-y-hidden`}

  &:before {
    ${tw`tw-absolute tw-left-0 tw-right-0 tw-bottom-0 tw-bg-page-paper-main tw-rounded-lg tw-shadow-lg`}
    content: "";
    top: 39px;
    z-index: -1;
  }
`;
StyledTableWrapper.displayName = "StyledTableWrapper";

export const StyledTable = styled.table<{
  $striped: boolean;
}>`
  ${tw`tw-w-full tw-p-0 tw-text-xs tw-pb-4`}
  border-spacing: 0;

  ${StyledTableHead} tr th,
  ${StyledTableBody} tr td {
    &:last-child {
      ${tw`tw-text-right`}
    }
  }

  td {
    ${tw`tw-py-3 tw-px-4`}
  }

  ${(props) =>
    props.$striped &&
    css`
      ${StyledTableBody}:not(.c-table__grouped__head) {
        tr:nth-child(odd) {
          ${tw`tw-bg-theme-200`}
        }
      }
    `}
`;
StyledTable.displayName = "StyledTable";

export const StyledDataTable = styled.div`
  ${FontStyle}
  ${BoxSizingStyle}

  &.c-table__collapsible {
    .c-table__head {
      tr {
        th {
          position: sticky;
          top: 0;
          z-index: 1;
        }
      }
    }
  }
  &.c-table__grouped {
    .c-table__head th {
      ${tw`tw-pb-4`}
      &:last-child {
        ${tw`tw-pr-0`}
      }
    }
  }
  &.c-data-table {
    position: relative;
    .c-table__head th {
      &:last-child {
        ${tw`tw-pr-0`}
      }
    }
  }
  .c-table__cell {
    ${tw`tw-relative`}
    &:hover {
      .c-table__icon {
        ${tw`tw-opacity-50`}
      }
    }
  }
  .c-table__overlay {
    ${tw`tw-absolute tw-w-full tw-left-0 tw-pointer-events-none`}
    top: 0px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background: rgba(0, 0, 0, 0.02);
    ${DARK_THEME_SELECTOR} & {
      background: rgba(0, 0, 0, 0.1);
    }
    z-index: 5;
  }
  .c-table__icon-wrapper {
    ${tw`tw-inline-block tw-select-none`}
  }
  .c-table__label {
    ${tw`tw-border-0 tw-border-0 tw-border-transparent tw-border-solid`}
  }
  .c-table__icon {
    transform: translateY(1px);
    ${tw`tw-ml-1`}
    fill: var(--color-theme-900);

    &:hover {
      ${tw`tw-opacity-100`}
    }
  }
  .c-table__icon--inactive {
    ${tw`tw-opacity-0`}
  }
  .c-table__icon--active {
    ${tw`tw-opacity-100`}
  }
  .c-table__icon--desc {
    transform: rotate(-180deg) scale(-1, 1);
  }
  .c-table__grouped__head {
    ${tw`tw-font-bold`}
    > :first-child {
      ${tw`tw-text-theme-900`}
    }
    a:first-child {
      ${tw`tw-text-primary`}
    }
  }
  .c-table__head--hidden {
    ${tw`tw-pointer-events-none tw-opacity-0`}
  }
  .c-table__collapse_icon--expanded {
    transition-duration: 0.3s;
    ${tw`tw-cursor-pointer`}
  }
  .c-table__collapse_icon--collapsed {
    transform: rotate(180deg);
    transition-duration: 0.3s;
    ${tw`tw-cursor-pointer`}
  }
  .c-table__head__icon-wrapper__icon {
    ${tw`tw-absolute tw-cursor-pointer`}
    transform: translate(-24px, -5px);
  }
  &.c-table__collapsible {
    .c-table__group {
      .c-table__row:last-child .c-table__cell {
        border-bottom: 1px solid var(--border-primary);
      }
      &:last-child {
        .c-table__row:last-child .c-table__cell {
          border-bottom: 0;
        }
      }
    }
    .c-table__grouped__head {
      tr td {
        border-bottom: 1px solid var(--border-primary);
      }
      .c-table__body__collapse td {
        border-bottom: 0;
      }
      &.last {
        tr td {
          border-bottom: 0 !important;
        }
      }
    }
    .c-table__grouped__head--expanded {
      tr:last-child td {
        border-bottom: 0;
      }
    }
  }
  .c-table__collapse_body {
    tr {
      background-color: transparent !important;
    }
    td:last-child {
      ${tw`tw-border-0`}
    }
    ${tw`tw-absolute`}
  }
  .c-table__row {
    td:first-child {
      position: sticky;
      left: 0;
      z-index: 1;
    }
    &:nth-child(odd) {
      td {
        ${tw`tw-bg-theme-200`}
      }
    }
    &:nth-child(even) {
      td {
        ${tw`tw-bg-page-paper-main`}
      }
    }
  }
  .c-table__row {
    th:first-child {
      position: sticky;
      left: 0;
      z-index: 2 !important;
    }
  }
  &.c-table__collapsible .c-table__body {
    tr {
      td:first-child,
      th:first-child {
        ${tw`tw-pl-6`}
      }
    }
  }
  .c-table__body__icon {
    ${tw`tw-p-0`}
    padding-left: 0 !important;
    transform: translateY(8px);
  }
  &.padding-b-0 {
    ${tw`tw-pb-0`}
  }
  .cursor-pointer {
    ${tw`tw-cursor-pointer`}
  }
  .text-center {
    ${tw`tw-text-center`}
  }
  .opacity-75 {
    ${tw`tw-opacity-75`}
  }
  .hidden {
    ${tw`tw-hidden`}
  }
`;
StyledDataTable.displayName = "StyledDataTable";
